import { FC, useMemo } from "react"
import { Trash2 } from "lucide-react"
import { selectIsSubmitting } from "../../../../redux/slices/integrations"
import {
  Integration,
  IntegrationStatus,
  SidePanelWithTabsProps
} from "../../../../types"
import { useAppSelector } from "../../../hooks"
import SidePanelWithTabs from "../../side-panel-with-tabs"
import { Settings, Status, Install, Troubleshoot } from "../tabs"

const TabSkeleton = () => (
  <div className="mt-2 animate-pulse space-y-5 p-4">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="space-y-2">
        <div className="h-3 w-24 rounded bg-gray-200" />
        <div className="h-9 rounded bg-gray-200" />
      </div>
    ))}
  </div>
)

interface Props {
  integration?: Integration
  isLoadingIntegration: boolean
  statusData?: IntegrationStatus[]
  isLoadingStatus: boolean
  open: boolean
  onClose: () => void
  onSubmitIntegration: () => void
  openDeleteModal: () => void
  onSaveSettings: (integration: Integration) => void
}

const EditIntegration: FC<Props> = ({
  integration,
  isLoadingIntegration,
  statusData,
  isLoadingStatus,
  open,
  onClose,
  onSubmitIntegration,
  openDeleteModal,
  onSaveSettings
}) => {
  const selectedIsSubmitting = useAppSelector(selectIsSubmitting)

  const isLoading = isLoadingIntegration || !integration

  const tabs = useMemo<SidePanelWithTabsProps["tabs"]>(
    () => [
      {
        id: "settings",
        label: "Settings",
        Content: () =>
          isLoading ? (
            <TabSkeleton />
          ) : (
            <Settings
              integration={integration}
              isLoading={isLoadingIntegration}
              onSaveSettings={onSaveSettings}
              open={open}
            />
          ),
        actions: isLoading
          ? undefined
          : [{ label: "Update", type: "primary", onClick: onSubmitIntegration }]
      },
      {
        id: "status",
        label: "Status",
        Content: () =>
          isLoading ? (
            <TabSkeleton />
          ) : (
            <Status
              integration={integration!}
              statusData={statusData}
              isLoading={isLoadingStatus}
            />
          )
      },
      {
        id: "troubleshoot",
        label: "Troubleshoot",
        Content: () =>
          isLoading ? (
            <TabSkeleton />
          ) : (
            <Troubleshoot integration={integration!} />
          )
      },
      {
        id: "install",
        label: "Install",
        Content: () =>
          isLoading ? <TabSkeleton /> : <Install integration={integration!} />
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      integration,
      isLoading,
      isLoadingIntegration,
      isLoadingStatus,
      onSaveSettings,
      onSubmitIntegration,
      statusData,
      open
    ]
  )

  const title = isLoading ? (
    <div className="h-5 w-44 animate-pulse rounded bg-gray-200" />
  ) : (
    <div className="flex items-center">
      {integration!.title}
      <div className="ml-4 cursor-pointer" onClick={openDeleteModal}>
        <Trash2 size={20} />
      </div>
    </div>
  )

  return (
    <SidePanelWithTabs
      isSubmitting={selectedIsSubmitting}
      title={title}
      open={open}
      onClose={onClose}
      tabs={tabs}
    />
  )
}

export default EditIntegration
